import { useState, useEffect, createContext, useContext } from 'react';

interface User {
  user_id: string;
  email?: string;
  name?: string;
  profile_picture?: string;
  is_admin?: boolean;
  paid_plan?: number; // 0: 일반 사용자, 1: light user, 2: AI Power User
  /** 강사로 지정된 쿠폰(스터디 기수) 코드. 비어 있으면 강사가 아님 */
  instructor_coupons?: string[];
  /** 서버가 판정한 AI Power User 여부 (관리자·수동 부여·체험·구독) */
  ai_power?: AiPower;
}

export interface AiPower {
  aiPower: boolean;
  source: 'admin' | 'manual' | 'subscription' | 'trial' | null;
  until: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentialOrUserId: string | any) => Promise<any>;
  logout: () => void;
  /** 체험 시작·구독처럼 권한이 바뀐 뒤 사용자 정보를 다시 받아 옵니다 */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 페이지 로드 시 저장된 사용자 정보 확인
    const savedUserId = localStorage.getItem('userId');
    const savedToken = localStorage.getItem('authToken');
    
    if (savedUserId && savedToken) {
      validateToken(savedToken);
    } else if (savedUserId) {
      console.log('Using saved userId without token');
      // localStorage에서 사용자 정보 로드
      const savedEmail = localStorage.getItem('userEmail');
      const savedName = localStorage.getItem('userName');
      const savedProfilePicture = localStorage.getItem('userProfilePicture');
      const savedIsAdmin = localStorage.getItem('isAdmin') === 'true';
      
      setUser({ 
        user_id: savedUserId,
        email: savedEmail || undefined,
        name: savedName || undefined,
        profile_picture: savedProfilePicture || undefined,
        is_admin: savedIsAdmin,
        paid_plan: 0
      });
      setIsLoading(false);
    } else {
      console.log('No saved auth data');
      setIsLoading(false);
    }
  }, []);

  const validateToken = async (token: string) => {
    try {
      // Google JWT 검증
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const userData = result.data.user || result.data;
          // localStorage에서 추가 정보 로드 (프로필 사진 등)
          const savedProfilePicture = localStorage.getItem('userProfilePicture');
          
          setUser({
            user_id: userData.userId || userData.user_id,
            email: userData.email,
            name: userData.name,
            profile_picture: userData.profile_picture || savedProfilePicture || undefined,
            is_admin: userData.isAdmin || userData.is_admin,
            paid_plan: userData.paid_plan ?? 0,
            instructor_coupons: userData.instructor_coupons || [],
            ai_power: userData.ai_power
          });
        } else {
          console.log('Token validation failed, clearing storage');
          localStorage.removeItem('userId');
          localStorage.removeItem('authToken');
        }
      } else {
        console.log('Token validation request failed, clearing storage');
        localStorage.removeItem('userId');
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      localStorage.removeItem('userId');
      localStorage.removeItem('authToken');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentialOrUserId: string | any) => {
    setIsLoading(true);
    try {
      if (typeof credentialOrUserId === 'string' && !credentialOrUserId.includes('.')) {
        localStorage.setItem('userId', credentialOrUserId);
        setUser({ user_id: credentialOrUserId });
      } else if (typeof credentialOrUserId === 'object' && credentialOrUserId.user_id) {
        localStorage.setItem('userId', credentialOrUserId.user_id);
        setUser(credentialOrUserId);
      } else {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: credentialOrUserId })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const userData = result.data;
            localStorage.setItem('userId', userData.user_id);
            localStorage.setItem('authToken', userData.token);
            setUser({
              user_id: userData.user_id,
              email: userData.email,
              name: userData.name,
              profile_picture: userData.profile_picture,
              is_admin: userData.is_admin,
              paid_plan: userData.paid_plan ?? 0,
              instructor_coupons: userData.instructor_coupons || [],
              ai_power: userData.ai_power
            });
            return null;
          } else {
            throw new Error(result.error?.message || 'Google login failed');
          }
        } else {
          throw new Error(`Google login failed: ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('authToken');
    if (token) await validateToken(token);
  };

  const logout = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('authToken');
    setUser(null);
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
  };
};

export { AuthContext };